/**
 * TournamentContext - Lightweight Tournament Metadata Management
 *
 * This context provides core tournament metadata using TanStack Query:
 * - Tournament settings and configuration
 * - Player information (for display across the app)
 * - Location data
 *
 * Data Sources:
 * - Tournament: One-time query with 5min staleTime (settings change rarely)
 * - Players: One-time queries with 5min staleTime (players change rarely)
 * - Locations: One-time queries with 5min staleTime (locations change rarely)
 *
 * Performance Optimizations:
 * - Separated from TeamsContext and MatchesContext for fine-grained re-renders
 * - Components only re-render when the specific data they need changes
 * - One-time queries: Reduces concurrent Firestore listeners
 *
 * For teams and matches data, use TeamsContext and MatchesContext respectively.
 */

"use client";

import { useTournamentContextPlayers } from "@/hooks/useTournamentPlayers";
import { locationsAdapter, tournamentsAdapter } from "@/lib/adapters/firestore";
import { Location, PublicUser, Tournament } from "@/types";
import { useQuery } from "@tanstack/react-query";
import React, { createContext, useContext, useMemo } from "react";
import { MatchesProvider } from "./MatchesContext";
import { TeamsProvider } from "./TeamsContext";

interface TournamentContextType {
  tournament: Tournament | null;
  players: Map<string, PublicUser>;
  locations: Location[];
  loading: boolean;
  error: string | null;
}

const TournamentContext = createContext<TournamentContextType | undefined>(
  undefined,
);

export { useMatchesContext } from "./MatchesContext";
export { useTeamsContext } from "./TeamsContext";

export const useTournamentContext = () => {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error(
      "useTournamentContext must be used within a TournamentProvider",
    );
  }
  return context;
};

export function TournamentProvider({
  children,
  tournamentId,
}: {
  children: React.ReactNode;
  tournamentId: string;
}) {
  // ===========================================================================
  // TOURNAMENT METADATA
  // ===========================================================================
  // Tournament settings change rarely, use one-time query
  // ===========================================================================

  const {
    data: tournament = null,
    isLoading: tournamentLoading,
    error: tournamentError,
  } = useQuery({
    queryKey: ["tournament", tournamentId],
    queryFn: () => tournamentsAdapter.getById(tournamentId),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // ===========================================================================
  // PLAYERS DATA
  // ===========================================================================
  // Players change rarely, use one-time query
  // ===========================================================================

  const {
    data: players = [],
    isLoading: playersLoading,
    error: playersError,
  } = useTournamentContextPlayers(tournamentId);

  // ===========================================================================
  // LOCATIONS DATA
  // ===========================================================================
  // Fetch locations associated with the tournament using a single query
  // ===========================================================================

  const {
    data: locations = [],
    isLoading: locationsLoading,
    error: locationsError,
  } = useQuery({
    queryKey: ["locations", tournamentId, tournament?.locationIds],
    queryFn: async () => {
      if (!tournament?.locationIds || tournament.locationIds.length === 0) {
        return [];
      }
      // Fetch all locations in a single query using "in" operator
      return locationsAdapter.getAll({
        filters: [
          { field: "__name__", operator: "in", value: tournament.locationIds },
        ],
      });
    },
    enabled: !!tournament?.locationIds && tournament.locationIds.length > 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Combine loading states
  const loading = playersLoading || tournamentLoading || locationsLoading;
  const error =
    playersError?.message ||
    tournamentError?.message ||
    locationsError?.message ||
    null;

  // Mapped players for easy lookup
  const mappedPlayers = useMemo(() => {
    const map = new Map<string, PublicUser>();
    players.forEach((player) => {
      map.set(player.id, player);
    });
    return map;
  }, [players]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<TournamentContextType>(
    () => ({
      tournament,
      players: mappedPlayers,
      locations,
      loading,
      error,
    }),
    [tournament, mappedPlayers, locations, loading, error],
  );

  return (
    <TournamentContext.Provider value={value}>
      {children}
    </TournamentContext.Provider>
  );
}

/**
 * CombinedTournamentProvider - Convenience wrapper for all tournament contexts
 *
 * Combines TournamentProvider, TeamsProvider, and MatchesProvider into a single
 * provider for easy use. This maintains the separation of concerns while providing
 * a simple API for consumers.
 *
 * Usage:
 * ```tsx
 * <CombinedTournamentProvider tournamentId={tournamentId}>
 *   <YourComponent />
 * </CombinedTournamentProvider>
 * ```
 *
 * Inside components, use the appropriate context:
 * - useTournamentContext() for tournament, players, locations
 * - useTeamsContext() for teams, currentUserTeam
 * - useMatchesContext() for matches
 */
export function CombinedTournamentProvider({
  children,
  tournamentId,
}: {
  children: React.ReactNode;
  tournamentId: string;
}) {
  return (
    <TournamentProvider tournamentId={tournamentId}>
      <TeamsProvider tournamentId={tournamentId}>
        <MatchesProvider tournamentId={tournamentId}>
          {children}
        </MatchesProvider>
      </TeamsProvider>
    </TournamentProvider>
  );
}
